import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidatorFn } from '@angular/forms';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { InputComponent } from '@libs/ui-lib';
import { PhoneMaskDirective } from 'libs/ui-lib/src/lib/directives';
import { PatientContactTypeEnum, PatientDetailsV2 } from '@ffd/api-client';
import { AgePipe } from '../../pipes';
import { HealthHistoryDialogService, SecondaryContactVM } from '../../health-history-dialog.service';
import {
  ProfileContact,
  contactValidator,
} from '../health-history-patient-details/components/health-history-patient-details-secondary-contacts/secondary-contact-validators';

export interface PatientInfoEditDialogData {
  patient: PatientDetailsV2;
  secondaryContacts: SecondaryContactVM[];
}

@Component({
  selector: 'lib-patient-info-edit-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule, SvgIconComponent, InputComponent, PhoneMaskDirective, AgePipe],
  templateUrl: './patient-info-edit-dialog.component.html',
  styleUrl: './patient-info-edit-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PatientInfoEditDialogComponent {
  private readonly dialogRef = inject<DialogRef<void>>(DialogRef);
  protected readonly data = inject<PatientInfoEditDialogData>(DIALOG_DATA);
  private readonly healthHistoryDialogService = inject(HealthHistoryDialogService);

  public readonly form = new FormGroup({
    emergencyName: new FormControl<string>('', { nonNullable: true }),
    emergencyPhone: new FormControl<string>('', { nonNullable: true }),

    pharmacyName: new FormControl<string>('', { nonNullable: true }),
    pharmacyPhone: new FormControl<string>('', { nonNullable: true }),

    physicianName: new FormControl<string>('', { nonNullable: true }),
    physicianPhone: new FormControl<string>('', { nonNullable: true }),
  });

  // Section visibility derived from the secondaryContacts the parent passed in (already filtered by PMS).
  protected readonly hasEmergency = this.data.secondaryContacts.some((c) => c.contactType === PatientContactTypeEnum.EMERGENCY_CONTACT);
  protected readonly hasPharmacy = this.data.secondaryContacts.some((c) => c.contactType === PatientContactTypeEnum.PREFERRED_PHARMACY);
  protected readonly hasPhysician = this.data.secondaryContacts.some((c) => c.contactType === PatientContactTypeEnum.PHYSICIAN_CONTACT);

  constructor() {
    const emergency = this.findContact(PatientContactTypeEnum.EMERGENCY_CONTACT);
    const pharmacy = this.findContact(PatientContactTypeEnum.PREFERRED_PHARMACY);
    const physician = this.findContact(PatientContactTypeEnum.PHYSICIAN_CONTACT);

    this.form.patchValue(
      {
        emergencyName: emergency?.contactName ?? '',
        emergencyPhone: emergency?.phoneNumber ?? '',

        pharmacyName: pharmacy?.contactName ?? '',
        pharmacyPhone: pharmacy?.phoneNumber ?? '',

        physicianName: physician?.contactName ?? '',
        physicianPhone: physician?.phoneNumber ?? '',
      },
      { emitEvent: false },
    );

    // Cross-field validators per contact section — each pair (name+phone) gets the same shared validator
    // attached to both controls. We also link each control's valueChanges to its partner's
    // updateValueAndValidity so editing one field immediately re-evaluates the partner's error state.
    // Only attach to sections that are visible for this PMS type.
    if (this.hasEmergency) {
      this.attachContactPair('emergencyName', 'emergencyPhone', false);
    }
    if (this.hasPharmacy) {
      this.attachContactPair('pharmacyName', 'pharmacyPhone', true);
    }
    if (this.hasPhysician) {
      this.attachContactPair('physicianName', 'physicianPhone', false);
    }
  }

  private attachContactPair(
    nameKey: 'emergencyName' | 'pharmacyName' | 'physicianName',
    phoneKey: 'emergencyPhone' | 'pharmacyPhone' | 'physicianPhone',
    isPharmacy: boolean,
  ): void {
    const nameCtrl = this.form.controls[nameKey];
    const phoneCtrl = this.form.controls[phoneKey];
    nameCtrl.addValidators(this.contactPairValidator(nameKey, phoneKey, isPharmacy));
    phoneCtrl.addValidators(this.contactPairValidator(nameKey, phoneKey, isPharmacy));
    nameCtrl.valueChanges.subscribe(() => phoneCtrl.updateValueAndValidity({ emitEvent: false }));
    phoneCtrl.valueChanges.subscribe(() => nameCtrl.updateValueAndValidity({ emitEvent: false }));
  }

  protected onSave(): void {
    if (this.form.invalid) {
      return;
    }
    const v = this.form.getRawValue();

    const updates: ProfileContact[] = [];
    if (this.hasEmergency) {
      this.collect(
        updates,
        v.emergencyName,
        v.emergencyPhone,
        PatientContactTypeEnum.EMERGENCY_CONTACT,
        this.findContact(PatientContactTypeEnum.EMERGENCY_CONTACT),
      );
    }
    if (this.hasPharmacy) {
      this.collect(
        updates,
        v.pharmacyName,
        v.pharmacyPhone,
        PatientContactTypeEnum.PREFERRED_PHARMACY,
        this.findContact(PatientContactTypeEnum.PREFERRED_PHARMACY),
      );
    }
    if (this.hasPhysician) {
      this.collect(
        updates,
        v.physicianName,
        v.physicianPhone,
        PatientContactTypeEnum.PHYSICIAN_CONTACT,
        this.findContact(PatientContactTypeEnum.PHYSICIAN_CONTACT),
      );
    }

    if (updates.length > 0) {
      this.healthHistoryDialogService.updateMultiplePatientContacts(updates);
    }
    this.dialogRef.close();
  }

  protected onCancel(): void {
    this.dialogRef.close();
  }

  private findContact(type: PatientContactTypeEnum): SecondaryContactVM | undefined {
    return this.data.secondaryContacts.find((c) => c.contactType === type);
  }

  private collect(
    updates: ProfileContact[],
    name: string,
    phone: string,
    contactType: PatientContactTypeEnum,
    original: SecondaryContactVM | undefined,
  ): void {
    const trimmedName = name.trim();
    const strippedPhone = phone.trim().replaceAll(/\D/g, '');

    const originalPhone = (original?.phoneNumber ?? '').replaceAll(/\D/g, '');
    if (trimmedName === (original?.contactName ?? '') && strippedPhone === originalPhone) {
      return;
    }

    if (trimmedName || strippedPhone) {
      updates.push({
        contactType,
        contactName: trimmedName,
        phoneNumber: strippedPhone,
      });
    }
  }

  // Adapter: wraps the shared `contactValidator(isPharmacy)` to read both controls of a pair.
  private contactPairValidator(
    nameKey: keyof typeof this.form.controls,
    phoneKey: keyof typeof this.form.controls,
    isPharmacy: boolean,
  ): ValidatorFn {
    return () => {
      const nameCtrl = this.form.controls[nameKey];
      const phoneCtrl = this.form.controls[phoneKey];
      const synthetic = { value: { name: nameCtrl.value as string, phone: phoneCtrl.value as string } } as unknown as AbstractControl;
      return contactValidator(isPharmacy)(synthetic);
    };
  }
}
